import {
  AlertInput,
  IonButton,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonList,
  IonListHeader,
  IonText,
  useIonAlert,
} from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep, isEmpty, round } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanEntitlementKey } from '../../../../../../api/graphql/generated';
import { useConfirmActionAlert } from '../../../../../../components/alert/confirmation-alert';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { CloseButton } from '../../../../../../components/button/close-button';
import {
  FeatureTour,
  FeatureTourRef,
} from '../../../../../../components/feature/feature-tour';
import { AppFeatureKey } from '../../../../../../components/feature/feature.types';
import { FormSubmitButton } from '../../../../../../components/form/form-submit.button';
import { IonFormTextSwitchField } from '../../../../../../components/form/ion-form-text-switch.field';
import { PaywallBlur } from '../../../../../../components/paywall/paywall-blur';
import { useHasEntitlement } from '../../../../../../components/plan/plan-entitlement-hooks';
import { isLocalDev } from '../../../../../../config/config';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import {
  ApplicationFeature,
  useHasApplicationSupport,
} from '../../../../../../pages/machine/application-support-context';
import { logger } from '../../../../../../util/logger';
import { QNear, Waypoint } from '../../../../../../util/machine-variable.utils';
import {
  FineTuneSettings,
  FineTunedPointRequired,
  PartUtil,
  WorkPiecePosition,
} from '../../../../../../util/part.util';
import { Pose, PoseUtil } from '../../../../../../util/pose.util';
import { useRegenerateMultipassSection } from '../../../../beacon/welding/component/flow-item/multipass/multipass.hooks';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import { WaypointCaptureRef } from '../../../hook/waypoint-capture.hooks';
import {
  FlowItem,
  FlowItemSharedParameterRef,
  WorkAngleType,
} from '../../../model/flow.types';
import {
  addStickoutToPose,
  getParameterValue,
} from '../../../util/flow-parameter.util';
import { formatNumber } from '../../../util/math';
import {
  createUpdatedItemIds,
  detectAffectedMultipassSections,
} from '../../../util/multipass-detection.util';
import { CaptureWaypointSpinner } from '../../button/capture-waypoint-button';
import './flow-item-fine-tune.css';
import { useFlowRefContext } from '../../flow/flow-ref';
import { PickerInput } from '../../widget/picker-input';
import { SliderInput } from '../../widget/slider-input';
import { useFlowItemFineTuneTourSteps } from './flow-item-fine-tune-tour';
import { useFineTuneScript } from './flow-item-fine-tune.hooks';

export interface FineTuneOnUpdatePointOptions {
  waypoint: Waypoint;
  qnear: QNear;
}

export type FineTuneOnUpdatePointCallback = (
  options: FineTuneOnUpdatePointOptions
) => void;

interface FlowItemFineTuneProps {
  item: FlowItem;
  onUpdatePoint: FineTuneOnUpdatePointCallback;
  stopProgramFirst?: boolean;
  dismiss?: () => void;
}

interface FineTuneForm {
  travelAngle: number;
  pathOffset: number;
  stickout: number;
  workAngle: number;
  relativeWorkAngle: number;
  workAngleType: WorkAngleType;
  workSurfacePosition: WorkPiecePosition;
  workSurfaceOffset: number;
  originalWaypoint: Waypoint;
  originalQnear: QNear;
  modifiedWaypoint: Waypoint;
  modifiedQnear: QNear;
}

interface ApplyProperty {
  travelAngle: boolean;
  workAngle: boolean;
  stickout: boolean;
}

interface ApplyOptions extends FineTuneForm {
  applyTo: 'sequence' | 'item';
  apply: ApplyProperty;
}

const FlowItemFineTune = (props: FlowItemFineTuneProps) => {
  const { dismiss, item, onUpdatePoint } = props;

  const { flow, flowItemService, flowAnalyticService, saveFlow } =
    useFlowServices();
  const { flowItemTextArr, selectedItemDetails } = useFlowRefContext();
  const { i18n } = useTranslation();

  const [saving, setSaving] = useState<boolean>(false);
  const captureRef = useRef<WaypointCaptureRef>();
  const formikRef = useRef<FormikProps<FineTuneForm> | null>(null);

  const [disablePaywallBlur, setDisablePaywallBlur] = useState<boolean>(false);
  const tourRef = useRef<FeatureTourRef>(null);

  const hasFineTuneEntitlement = useHasEntitlement(
    PlanEntitlementKey.WELDING_FINE_TUNE
  );

  const confirmActionAlert = useConfirmActionAlert();
  const { regenerateMultipassSection } = useRegenerateMultipassSection({
    flowItemService,
  });

  const setFormikInnerRef = useCallback(
    (instance: FormikProps<FineTuneForm> | null) => {
      formikRef.current = instance;
    },
    []
  );

  const itemLabel = useMemo(() => {
    const fineTuneLabel = i18n.t('common:fine_tune');

    const label =
      flowItemTextArr[selectedItemDetails.selectedItemActualIndex]?.label;

    if (label) {
      return label;
    } else {
      return fineTuneLabel;
    }
  }, [flowItemTextArr, selectedItemDetails, i18n]);

  const [showApplyToAllAlert] = useIonAlert();

  const fineTuneProps = useMemo(() => {
    return flowItemService.getFineTuneProps(flow, item);
  }, [flow, item, flowItemService]);

  // Initial baseline angles - only used for initialValues
  const { travelAngle: initialTravelAngle, workAngle: initialWorkAngle } =
    useMemo(() => {
      return flowItemService.getFineTuneAngles(fineTuneProps);
    }, [fineTuneProps, flowItemService]);

  const initialValues = useMemo<FineTuneForm>(() => {
    const waypoint = getParameterValue<Waypoint>(
      item,
      FlowItemSharedParameterRef.WAYPOINT
    );

    const qnear = getParameterValue<QNear>(
      item,
      FlowItemSharedParameterRef.QNEAR
    );

    const stickout = getParameterValue<number>(
      item,
      FlowItemSharedParameterRef.STICKOUT
    );

    const convertedWorkSurfacePosition =
      fineTuneProps.workPiecePosition ?? WorkPiecePosition.HORIZONTAL;

    // // we treat flat as horizontal for the purposes of fine tune
    // if (convertedWorkSurfacePosition === WorkPiecePosition.FLAT) {
    //   convertedWorkSurfacePosition = WorkPiecePosition.HORIZONTAL;
    // }

    const form: FineTuneForm = {
      originalWaypoint: waypoint,
      originalQnear: qnear,
      modifiedWaypoint: waypoint,
      modifiedQnear: qnear,
      pathOffset: 0,
      stickout: stickout ?? 0,
      travelAngle: initialTravelAngle ? formatNumber(initialTravelAngle, 1) : 0,
      workAngle: initialWorkAngle ? formatNumber(initialWorkAngle, 1) : 0,
      relativeWorkAngle: 0,
      workAngleType: WorkAngleType.RELATIVE,
      workSurfacePosition: convertedWorkSurfacePosition,
      workSurfaceOffset: fineTuneProps.workPieceOffset ?? 0,
    };

    return form;
  }, [
    item,
    initialTravelAngle,
    initialWorkAngle,
    fineTuneProps.workPiecePosition,
    fineTuneProps.workPieceOffset,
  ]);

  const onClose = useCallback(async () => {
    if (typeof dismiss === 'function') {
      dismiss();
    }
  }, [dismiss]);

  const { moveToPosition } = useFineTuneScript({
    onProgramStop: onClose,
    toolConfig: fineTuneProps.toolConfig,
    executingIndex: fineTuneProps.executingIndex,
  });

  const hasApplicationSupport = useHasApplicationSupport({
    feature: ApplicationFeature.FINE_TUNE,
    defaultValue: true,
  });

  const isDisabled = useMemo(() => {
    // disable widgets for non-default item types. this means
    // only allow touching up on the main item type set in the service
    // which would be a weld or cut type today
    return !fineTuneProps.supported || !hasApplicationSupport;
  }, [fineTuneProps.supported, hasApplicationSupport]);

  const applySettingsToPoint = useCallback(
    async (options: ApplyOptions) => {
      setSaving(true);

      const { apply, applyTo, modifiedWaypoint, modifiedQnear, stickout } =
        options;

      const clonedItem = cloneDeep(item);

      const pose = PartUtil.convertWaypointToPose(modifiedWaypoint);

      if (fineTuneProps.supported && hasFineTuneEntitlement) {
        let transWaypoint = modifiedWaypoint;

        // if we are storing stickout as a separate parameter then we need to
        // remove it from the pose
        if (!fineTuneProps.poseIncludesStickout) {
          const trans = addStickoutToPose(pose, stickout);
          transWaypoint = PartUtil.convertPoseToWaypoint(trans);

          flowItemService.setParameterValue(
            clonedItem,
            FlowItemSharedParameterRef.STICKOUT,
            stickout
          );
        }

        flowItemService.setParameterValue(
          clonedItem,
          FlowItemSharedParameterRef.WAYPOINT,
          transWaypoint
        );

        flowItemService.setParameterValue(
          clonedItem,
          FlowItemSharedParameterRef.QNEAR,
          modifiedQnear
        );

        let itemsToSave: FlowItem[] = [clonedItem];

        if (applyTo === 'sequence') {
          const { travelAngle, workAngle } = options;

          if (travelAngle !== undefined) {
            let stickoutToApply = apply.stickout ? stickout : undefined;

            // if stickout is not a separate parameter then we can set this to
            // undefined so we don't apply it to the sequence
            if (fineTuneProps.poseIncludesStickout) {
              stickoutToApply = undefined;
            }

            const result = flowItemService.setFineTuneSettingsForFlowItems(
              flow,
              item,
              {
                travelAngle: apply.travelAngle ? travelAngle : undefined,
                workAngle: apply.workAngle ? workAngle : undefined,
                stickout: stickoutToApply,
              }
            );

            itemsToSave = itemsToSave.concat(result);
          }
        }

        itemsToSave = itemsToSave.map((i) => {
          return {
            ...i,
            attributes: {
              ...i.attributes,
              workSurfacePosition: options.workSurfacePosition,
              workSurfaceOffset: options.workSurfaceOffset,
            },
          };
        });

        if (itemsToSave.length > 0) {
          const clonedFlow = cloneDeep(flow);
          const updatedFlowItems = flow.items.map((i) => {
            for (const item of itemsToSave) {
              if (item.id === i.id) {
                return item;
              }
            }
            return i;
          });

          // Check for multipass sections that need their originalItems updated
          const updatedItemIds = createUpdatedItemIds(itemsToSave);
          const { updatedItems, multipassSectionsToUpdate } =
            detectAffectedMultipassSections(updatedFlowItems, updatedItemIds);

          // If there are multipass sections to update, prompt user before saving
          if (multipassSectionsToUpdate.length > 0) {
            logger.info(
              'Detected multipass sections that need regeneration from touch up',
              {
                updatedItemCount: itemsToSave.length,
                multipassSectionsUpdated: multipassSectionsToUpdate.length,
                multipassSectionIds: multipassSectionsToUpdate.map(
                  (section) => section.id
                ),
              }
            );

            // Prompt user to regenerate multipass sections before saving
            const promptForMultipassRegeneration = confirmActionAlert({
              header:
                getLocalizedText('welding:multipass_update') ??
                'Multipass Update',
              message:
                getLocalizedText('welding:multipass_update_message', {
                  count: multipassSectionsToUpdate.length,
                  plural: multipassSectionsToUpdate.length > 1 ? 's' : '',
                }) ??
                `The root pass has changed for ${
                  multipassSectionsToUpdate.length
                } multipass section${
                  multipassSectionsToUpdate.length > 1 ? 's' : ''
                }. Do you want to apply the changes to all passes?`,
              confirmText:
                getLocalizedText('welding:multipass_apply_changes') ??
                'Apply Changes',
              cancelText:
                getLocalizedText('welding:multipass_keep_original') ??
                'Keep Original',
              confirmationAction: async () => {
                // Regenerate each affected multipass section, then save once
                let finalUpdatedItems = updatedItems;
                let currentFlowItems = updatedFlowItems;

                for (const multipassSection of multipassSectionsToUpdate) {
                  finalUpdatedItems = await regenerateMultipassSection(
                    currentFlowItems,
                    multipassSection,
                    finalUpdatedItems
                  );
                  // Update currentFlowItems for the next iteration
                  currentFlowItems = finalUpdatedItems;
                }

                // Save once with all changes including regenerated multipass sections
                await saveFlow(
                  { ...clonedFlow, items: finalUpdatedItems },
                  {
                    saveAsDelta: true,
                  }
                );
              },
              onCancel: async () => {
                // Save just the updated items without regenerating multipass sections
                await saveFlow(
                  { ...clonedFlow, items: updatedItems },
                  {
                    saveAsDelta: true,
                  }
                );
              },
            });

            // Show the confirmation dialog
            promptForMultipassRegeneration();
          } else {
            // No multipass sections affected, save normally
            await saveFlow(
              { ...clonedFlow, items: updatedItems },
              {
                saveAsDelta: true,
              }
            );
          }
        }
      } else {
        onUpdatePoint({
          qnear: modifiedQnear,
          waypoint: modifiedWaypoint,
        });
      }

      setSaving(false);
      onClose();
    },
    [
      flow,
      flowItemService,
      item,
      saveFlow,
      onClose,
      fineTuneProps.supported,
      fineTuneProps.poseIncludesStickout,
      hasFineTuneEntitlement,
      onUpdatePoint,
      confirmActionAlert,
      regenerateMultipassSection,
    ]
  );

  const onFormSubmit = useCallback(
    async (form: FineTuneForm) => {
      const baseOptions: ApplyOptions = {
        ...form,
        applyTo: 'item',
        apply: {
          stickout: false,
          workAngle: false,
          travelAngle: false,
        },
      };

      // detect changes to form to determine which inputs should be
      // auto selected for applying to the sequence
      const hasTravelAngleChanged =
        initialValues.travelAngle !== form.travelAngle;
      const hasStickoutChanged = initialValues.stickout !== form.stickout;
      const hasWorkAngleChanged = initialValues.workAngle !== form.workAngle;
      const hasWorkAnglePositionChanged =
        initialValues.workSurfacePosition !== form.workSurfacePosition;
      const hasWorkAngleOffsetChanged =
        initialValues.workSurfaceOffset !== form.workSurfaceOffset;

      const applyToWorkAngle =
        hasWorkAngleChanged ||
        hasWorkAnglePositionChanged ||
        hasWorkAngleOffsetChanged;

      const hasChange =
        hasTravelAngleChanged || hasStickoutChanged || applyToWorkAngle;

      const checkboxInputVals = {
        applyToTravelAngle: hasTravelAngleChanged,
        applyToWorkAngle,
        applyToStickout: hasStickoutChanged,
      };

      let alertInputs: AlertInput[] = [
        {
          name: 'applyTravelAngle',
          label: `${i18n.t(fineTuneProps.ui.travelAngle.label)}`,
          checked: checkboxInputVals.applyToTravelAngle,
          type: 'checkbox',
          handler: (data) => {
            checkboxInputVals.applyToTravelAngle = data.checked ?? false;
          },
        },
        {
          name: 'applyWorkAngle',
          label: `${i18n.t(fineTuneProps.ui.workAngle.label)}`,
          checked: checkboxInputVals.applyToWorkAngle,
          type: 'checkbox',
          handler: (data) => {
            checkboxInputVals.applyToWorkAngle = data.checked ?? false;
          },
        },
        {
          name: 'applyStickout',
          label: `${i18n.t(fineTuneProps.ui.stickout.label)}`,
          checked: checkboxInputVals.applyToStickout,
          type: 'checkbox',
          handler: (data) => {
            checkboxInputVals.applyToStickout = data.checked ?? false;
          },
        },
      ];

      if (form.workAngleType === WorkAngleType.RELATIVE) {
        alertInputs = alertInputs.filter(
          (input) => input.name !== 'applyWorkAngle'
        );
      }

      if (fineTuneProps.supported && hasFineTuneEntitlement && hasChange) {
        showApplyToAllAlert({
          message: i18n.t(fineTuneProps.ui.applyTo.label),
          inputs: alertInputs,
          buttons: [
            {
              text: i18n.t('common:no'),
              role: 'cancel',
              handler: () => {
                applySettingsToPoint(baseOptions);
              },
            },
            {
              text: i18n.t('common:yes'),
              handler: () => {
                applySettingsToPoint({
                  ...baseOptions,
                  applyTo: 'sequence',
                  apply: {
                    stickout: checkboxInputVals.applyToStickout,
                    workAngle: checkboxInputVals.applyToWorkAngle,
                    travelAngle: checkboxInputVals.applyToTravelAngle,
                  },
                });
              },
            },
          ],
          backdropDismiss: false,
        });
      } else {
        applySettingsToPoint(baseOptions);
      }
    },
    [
      applySettingsToPoint,
      showApplyToAllAlert,
      i18n,
      fineTuneProps,
      initialValues,
      hasFineTuneEntitlement,
    ]
  );

  const fineTunePoint = useCallback(
    (form: FineTuneForm) => {
      let modifiedPose: Pose | undefined;
      let startPose = PartUtil.convertWaypointToPose(fineTuneProps.startPoint);
      const required = fineTuneProps.pointToProcess;

      let workAngle_relative = 0;
      let workAngle_fixed: number | undefined = form.workAngle ?? undefined;
      if (form.workAngleType === 'relative') {
        workAngle_relative = form.relativeWorkAngle;
        workAngle_fixed = undefined;
      }
      const fineTuneSettings: FineTuneSettings = {
        travelAngle: 0, // we set travel angle to an exact value
        alongLineOfTravel: form.pathOffset,
        alongTool: 0,
        workAngle: workAngle_relative,
        axisAngle: 0,
        squareToLine: 0,
      };

      if (fineTuneProps.type === 'radial') {
        if (fineTuneProps.midPoint && fineTuneProps.endPoint) {
          let midPose = PartUtil.convertWaypointToPose(fineTuneProps.midPoint);
          let endPose = PartUtil.convertWaypointToPose(fineTuneProps.endPoint);
          // set travel and work angle against original pose
          const desiredTorchAnglePose = PartUtil.setDesiredTorchAngles_radial(
            startPose,
            midPose,
            endPose,
            required,
            form.travelAngle,
            workAngle_fixed,
            form.workSurfacePosition,
            form.workSurfaceOffset
          );
          if (required === FineTunedPointRequired.START_POINT) {
            startPose = desiredTorchAnglePose;
          } else if (required === FineTunedPointRequired.MID_POINT) {
            midPose = desiredTorchAnglePose;
          } else if (required === FineTunedPointRequired.END_POINT) {
            endPose = desiredTorchAnglePose;
          }

          // adjust additional settings relative to new pose with travel angle
          modifiedPose = PartUtil.getFineTunedPoseToPath_radial(
            startPose,
            midPose,
            endPose,
            fineTuneSettings,
            required,
            form.workSurfacePosition
          );
        }
      } else {
        if (fineTuneProps.endPoint !== undefined) {
          const endPose = PartUtil.convertWaypointToPose(
            fineTuneProps.endPoint
          );
          // set travel and work angle against original pose
          const desiredTorchAngles = PartUtil.setDesiredTorchAngles_linear(
            startPose,
            endPose,
            required,
            fineTuneProps.midPoint
              ? PartUtil.convertWaypointToPose(fineTuneProps.midPoint)
              : undefined,
            form.travelAngle,
            workAngle_fixed,
            form.workSurfacePosition,
            form.workSurfaceOffset
          );
          // adjust additional settings relative to new pose with travel angle
          modifiedPose = PartUtil.getFineTunedPoseToPath_linear(
            desiredTorchAngles,
            endPose,
            fineTuneSettings,
            required as any,
            form.workSurfacePosition
          );
        } else {
          const ghostPose = PoseUtil.pose_trans(
            startPose,
            [0.05, 0, 0, 0, 0, 0]
          );
          fineTuneSettings.travelAngle = form.travelAngle;
          modifiedPose = PartUtil.fineTunePoint(
            startPose,
            ghostPose,
            fineTuneSettings,
            FineTunedPointRequired.START_POINT
          );
        }
      }

      if (modifiedPose) {
        const modifiedPoseWithStickout = addStickoutToPose(
          modifiedPose,
          -form.stickout
        );

        moveToPosition(modifiedPoseWithStickout);

        if (formikRef.current) {
          // update the form to have the modified waypoint. as we adjust
          // additional settings we can use this point as the basis versus the
          // original point if needed.
          formikRef.current.setFieldValue(
            'modifiedWaypoint',
            PartUtil.convertPoseToWaypoint(modifiedPose)
          );
        }
      }
    },
    [fineTuneProps, moveToPosition]
  );

  const setModifiedWaypointOnFormAndSubmit = useCallback(
    async (waypoint: Waypoint, qnear: QNear) => {
      if (formikRef.current) {
        const { setFieldValue, submitForm, values } = formikRef.current;

        // set field values to make sure internal formik state
        // is updated
        await setFieldValue('modifiedWaypoint', waypoint);
        await setFieldValue('modifiedQnear', qnear);

        // force set on values object since internal state
        // will not have synced by the time we submit form
        values.modifiedWaypoint = waypoint;
        values.modifiedQnear = qnear;

        submitForm();
      }
    },
    []
  );

  const featureTourSteps = useFlowItemFineTuneTourSteps(tourRef);

  const handleFixedAngleChange = useCallback(
    (value: number) => {
      if (!formikRef.current) {
        return;
      }
      fineTunePoint({
        ...formikRef.current.values,
        workAngle: value,
      });
      if (initialWorkAngle !== undefined) {
        const relativeWorkAngle = round(value - initialWorkAngle);
        formikRef.current?.setFieldValue(
          'relativeWorkAngle',
          relativeWorkAngle
        );
      }
      flowAnalyticService.trackFineTune('workAngle');
    },
    [fineTunePoint, flowAnalyticService, initialWorkAngle]
  );

  const handleRelativeAngleChange = useCallback(
    (value: number) => {
      if (!formikRef.current) {
        return;
      }
      fineTunePoint({
        ...formikRef.current.values,
        relativeWorkAngle: value,
      });
      if (initialWorkAngle !== undefined) {
        const fixedAngle = round(initialWorkAngle + value);
        formikRef.current.setFieldValue('workAngle', fixedAngle);
      }
      flowAnalyticService.trackFineTune('workAngle');
    },
    [fineTunePoint, flowAnalyticService, initialWorkAngle]
  );

  const handleWorkSurfacePositionChange = useCallback(
    (val: WorkPiecePosition) => {
      if (!formikRef.current) return;

      // Recalculate baseline angles with new position
      const newAngles = flowItemService.getFineTuneAngles({
        ...fineTuneProps,
        startPoint: formikRef.current.values.modifiedWaypoint,
        workPiecePosition: val,
        workPieceOffset: formikRef.current.values.workSurfaceOffset,
      });

      // Update angle fields without moving robot
      formikRef.current.setFieldValue(
        'travelAngle',
        newAngles.travelAngle ? formatNumber(newAngles.travelAngle, 1) : 0
      );
      formikRef.current.setFieldValue(
        'workAngle',
        newAngles.workAngle ? formatNumber(newAngles.workAngle, 1) : 0
      );

      flowAnalyticService.trackFineTune('partPosition');
    },
    [flowItemService, fineTuneProps, flowAnalyticService]
  );

  const handleWorkSurfaceOffsetChange = useCallback(
    (val: number) => {
      if (!formikRef.current) return;

      // Recalculate baseline angles with new offset
      const newAngles = flowItemService.getFineTuneAngles({
        ...fineTuneProps,
        startPoint: formikRef.current.values.modifiedWaypoint,
        workPiecePosition: formikRef.current.values.workSurfacePosition,
        workPieceOffset: val,
      });

      // Update angle fields without moving robot
      formikRef.current.setFieldValue(
        'travelAngle',
        newAngles.travelAngle ? formatNumber(newAngles.travelAngle, 1) : 0
      );
      formikRef.current.setFieldValue(
        'workAngle',
        newAngles.workAngle ? formatNumber(newAngles.workAngle, 1) : 0
      );

      flowAnalyticService.trackFineTune('partOffset');
    },
    [flowItemService, fineTuneProps, flowAnalyticService]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      innerRef={setFormikInnerRef}
      enableReinitialize={true}
    >
      {({ dirty, errors, values, resetForm }) => {
        const isReadyToSubmit = dirty && isEmpty(errors);
        return (
          <div className="fine-tune-container">
            <Form>
              <IonListHeader className="panel-header">
                <IonLabel>{itemLabel}</IonLabel>
                <IonButton
                  onClick={() => {
                    const pose = PartUtil.convertWaypointToPose(
                      values.originalWaypoint
                    );

                    const trans = addStickoutToPose(
                      pose,
                      -fineTuneProps.stickout
                    );

                    moveToPosition(trans);
                    resetForm();
                  }}
                >
                  {i18n.t('common:move_to_original')}
                </IonButton>
              </IonListHeader>
              <div className="header-divider" />
              <div className="help-container">
                <IonText className="help-text">
                  {i18n.t('common:fine_tune_capture_desc')}
                </IonText>
                <div className="ion-padding-bottom" />
                <CaptureWaypointSpinner
                  className="spinner"
                  innerRef={captureRef}
                  onCapture={setModifiedWaypointOnFormAndSubmit}
                />
              </div>
              {!isDisabled && (
                <>
                  <FeatureTour
                    ref={tourRef}
                    appFeatureKey={AppFeatureKey.WELDING_FINE_TUNE}
                    steps={featureTourSteps}
                    onClose={() => {
                      setDisablePaywallBlur(false);
                    }}
                    popoverProps={{
                      backdropDismiss: false,
                    }}
                    // TODO determine how to enable since we have current users
                    // of this feature
                    disableTourAlert={true}
                  />
                  <PaywallBlur
                    blurId="fine-tune-paywall"
                    planEntitlementKey={PlanEntitlementKey.WELDING_FINE_TUNE}
                    disableBlur={disablePaywallBlur}
                    customAction={() => {
                      return (
                        <IonButton
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDisablePaywallBlur(true);
                            tourRef.current?.startTour();
                          }}
                        >
                          {i18n.t('common:fine_tune')}
                        </IonButton>
                      );
                    }}
                    disableClick={true}
                  >
                    <IonList>
                      <SliderInput
                        name="travelAngle"
                        label={i18n.t(fineTuneProps.ui.travelAngle.label)}
                        min={-55}
                        max={55}
                        step={1}
                        onChange={(val) => {
                          fineTunePoint({
                            ...values,
                            travelAngle: val,
                          });
                          flowAnalyticService.trackFineTune('travelAngle');
                        }}
                        showIncrementIcons={true}
                      />
                      <>
                        <IonItemGroup>
                          <IonItem className="work-angle-header" lines="none">
                            <IonLabel>
                              {i18n.t(fineTuneProps.ui.workAngle.label)}
                            </IonLabel>
                            <IonFormTextSwitchField
                              name="workAngleType"
                              leftButton={{
                                value: WorkAngleType.FIXED,
                                label: i18n.t('common:fixed'),
                              }}
                              rightButton={{
                                value: WorkAngleType.RELATIVE,
                                label: i18n.t('common:relative'),
                              }}
                            />
                          </IonItem>
                          {values.workAngleType === WorkAngleType.FIXED ? (
                            <>
                              <SliderInput
                                name="workAngle"
                                label={i18n.t('common:angle')}
                                min={-90}
                                max={90}
                                step={1}
                                onChange={handleFixedAngleChange}
                                showIncrementIcons={true}
                                isNested={true}
                              />
                              <PickerInput<WorkPiecePosition>
                                name="workSurfacePosition"
                                label={i18n.t('common:position')}
                                options={[
                                  {
                                    label: i18n.t('common:horizontal'),
                                    value: WorkPiecePosition.HORIZONTAL,
                                  },
                                  {
                                    label: i18n.t('common:flat'),
                                    value: WorkPiecePosition.FLAT,
                                  },
                                  {
                                    label: i18n.t('common:vertical'),
                                    value: WorkPiecePosition.VERTICAL,
                                  },
                                  {
                                    label: i18n.t('common:overhead'),
                                    value: WorkPiecePosition.OVERHEAD,
                                  },
                                ]}
                                ionItemProps={{
                                  lines: 'inset',
                                }}
                                onChange={handleWorkSurfacePositionChange}
                                isNested={true}
                              />
                              <SliderInput
                                name="workSurfaceOffset"
                                label={i18n.t('common:part_offset')}
                                min={-90}
                                max={90}
                                step={1}
                                onChange={handleWorkSurfaceOffsetChange}
                                showIncrementIcons={true}
                                ionItemProps={{
                                  lines: 'inset',
                                }}
                                isNested={true}
                              />
                            </>
                          ) : (
                            <SliderInput
                              name="relativeWorkAngle"
                              label={i18n.t('common:angle_offset')}
                              min={-45}
                              max={45}
                              step={1}
                              onChange={handleRelativeAngleChange}
                              showIncrementIcons={true}
                              isNested={true}
                            />
                          )}
                        </IonItemGroup>
                      </>
                      <SliderInput
                        name="pathOffset"
                        label={i18n.t(fineTuneProps.ui.path.label)}
                        min={-20}
                        max={20}
                        step={1}
                        onChange={(val) => {
                          fineTunePoint({
                            ...values,
                            pathOffset: val,
                          });
                          flowAnalyticService.trackFineTune('path');
                        }}
                        showIncrementIcons={true}
                      />
                      <SliderInput
                        name="stickout"
                        label={i18n.t(fineTuneProps.ui.stickout.label)}
                        min={-10}
                        max={10}
                        step={1}
                        onChange={(val) => {
                          fineTunePoint({
                            ...values,
                            stickout: val,
                          });
                          flowAnalyticService.trackFineTune('stickout');
                        }}
                        snaps={true}
                        showIncrementIcons={true}
                      />
                    </IonList>
                  </PaywallBlur>
                </>
              )}
              <div className="ion-padding-top" />
              <RightAlignedButtons>
                <CloseButton
                  label={i18n.t('common:cancel')}
                  onClose={onClose}
                />
                {isLocalDev() && (
                  <FormSubmitButton
                    loading={saving}
                    buttonProps={{
                      color: isReadyToSubmit ? 'primary' : 'medium',
                      disabled: !isReadyToSubmit || saving,
                      fill: isReadyToSubmit ? 'solid' : 'outline',
                      // change type to button so we can properly override
                      // onclick
                      type: 'button',
                      onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        // force press our waypoint capture so which will cause
                        // our capture callback to fire and submit the form
                        // with the current position
                        captureRef.current?.forcePress();
                      },
                    }}
                    placement="none"
                  >
                    {i18n.t('common:save')}
                  </FormSubmitButton>
                )}
              </RightAlignedButtons>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

export { FlowItemFineTune };
